import {utils} from 'ethers';
import {computed, makeAutoObservable, observable} from 'mobx';
import {tokenDecimals} from '../helpers/config/contracts.config';
import {removeTrailingZeros} from '../helpers/formatters';

export const convertNrsBalanceToDecimal = (balance: string) => {
    return removeTrailingZeros(utils.formatUnits(balance, tokenDecimals.nrs));
}

const APR_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

interface AprCache {
    apr: string;
    yearlyEarnEstimate: string;
    totalEarned: string;
    updatedAt: number;
}

class EarnBalanceStore {
    latestUpdateAtNrsUsdc = 0;
    latestUpdateAtNxusdUsdc = 0;
    nrsTokenBalance = '0';
    lpNrsUsdcTokenBalance = '0';
    lpNrsUsdcStakingBalance = '0';
    lpNxusdUsdcTokenBalance = '0';
    lpNxusdUsdcStakingBalance = '0';

    // APR cache for LP pools
    nrsUsdcAprCache: AprCache | null = null;
    nxusdUsdcAprCache: AprCache | null = null;

    get lpNrsUsdcTokenBalanceDecimal() {
        return convertNrsBalanceToDecimal(this.lpNrsUsdcTokenBalance);
    }

    get lpNrsUsdcStakingBalanceDecimal() {
        return convertNrsBalanceToDecimal(this.lpNrsUsdcStakingBalance);
    }

    get lpNxusdUsdcTokenBalanceDecimal() {
        return convertNrsBalanceToDecimal(this.lpNxusdUsdcTokenBalance);
    }

    get lpNxusdUsdcStakingBalanceDecimal() {
        return convertNrsBalanceToDecimal(this.lpNxusdUsdcStakingBalance);
    }

    get nrsTokenBalanceDecimal() {
        return convertNrsBalanceToDecimal(this.nrsTokenBalance);
    }

    constructor() {
        makeAutoObservable(this, {
            nrsTokenBalance: observable,
            lpNrsUsdcTokenBalance: observable,
            lpNrsUsdcStakingBalance: observable,
            lpNxusdUsdcTokenBalance: observable,
            lpNxusdUsdcStakingBalance: observable,
            nrsUsdcAprCache: observable,
            nxusdUsdcAprCache: observable,

            nrsTokenBalanceDecimal: computed,
            lpNrsUsdcTokenBalanceDecimal: computed,
            lpNrsUsdcStakingBalanceDecimal: computed,
            lpNxusdUsdcTokenBalanceDecimal: computed,
            lpNxusdUsdcStakingBalanceDecimal: computed,
        });
    }

    public resetState() {
        this.latestUpdateAtNrsUsdc = 0;
        this.latestUpdateAtNxusdUsdc = 0;
        this.nrsTokenBalance = '0';
        this.lpNrsUsdcTokenBalance = '0';
        this.lpNrsUsdcStakingBalance = '0';
        this.lpNxusdUsdcTokenBalance = '0';
        this.lpNxusdUsdcStakingBalance = '0';
        this.nrsUsdcAprCache = null;
        this.nxusdUsdcAprCache = null;
    }

    // APR Cache methods
    setNrsUsdcAprCache(apr: string, yearlyEarnEstimate: string, totalEarned: string) {
        this.nrsUsdcAprCache = { apr, yearlyEarnEstimate, totalEarned, updatedAt: Date.now() };
    }

    setNxusdUsdcAprCache(apr: string, yearlyEarnEstimate: string, totalEarned: string) {
        this.nxusdUsdcAprCache = { apr, yearlyEarnEstimate, totalEarned, updatedAt: Date.now() };
    }

    getNrsUsdcAprCache(): AprCache | null {
        if (this.nrsUsdcAprCache && Date.now() - this.nrsUsdcAprCache.updatedAt < APR_CACHE_TTL) {
            return this.nrsUsdcAprCache;
        }
        return null;
    }

    getNxusdUsdcAprCache(): AprCache | null {
        if (this.nxusdUsdcAprCache && Date.now() - this.nxusdUsdcAprCache.updatedAt < APR_CACHE_TTL) {
            return this.nxusdUsdcAprCache;
        }
        return null;
    }

    invalidateNrsUsdcAprCache() {
        this.nrsUsdcAprCache = null;
    }

    invalidateNxusdUsdcAprCache() {
        this.nxusdUsdcAprCache = null;
    }

    invalidateAllAprCache() {
        this.nrsUsdcAprCache = null;
        this.nxusdUsdcAprCache = null;
    }

    setNrsTokenBalanceDecimal(newBalance: string) {
        this.nrsTokenBalance = newBalance;
    }

    setLpNrsUsdcTokenBalanceDecimal(newBalance: string) {
        this.lpNrsUsdcTokenBalance = newBalance;
    }

    setLpNrsUsdcStakingBalanceDecimal(newBalance: string) {
        this.lpNrsUsdcStakingBalance = newBalance;
    }

    setLpNxusdUsdcTokenBalanceDecimal(newBalance: string) {
        this.lpNxusdUsdcTokenBalance = newBalance;
    }

    setLpNxusdUsdcStakingBalanceDecimal(newBalance: string) {
        this.lpNxusdUsdcStakingBalance = newBalance;
    }

    async fetchBalancesNRS_USDC(nrsContract: any, lpNrsUsdcContract: any, lpNrsUsdcStakingContract: any, address: string) {
        this.latestUpdateAtNrsUsdc = Date.now()
        // {
        //     const balance = await nrsContract.read.balanceOf([address]);
        //     this.setNrsTokenBalanceDecimal(balance.toString());
        // }
        {
            const balance = await lpNrsUsdcContract.read.balanceOf([address]);
            this.setLpNrsUsdcTokenBalanceDecimal(balance.toString());
        }

        await this.fetchNrsUsdcStakingBalance(lpNrsUsdcStakingContract, address);
    }

    async fetchNrsUsdcStakingBalance(lpNrsUsdcStakingContract: any, address: string) {
        const [amount] = await lpNrsUsdcStakingContract.read.poolStakers([address]);
        this.setLpNrsUsdcStakingBalanceDecimal(amount.toString());
    }

    async fetchBalancesNRS_USDCThrottled(nrsContract: any, lpNrsdUsdcContract: any, lpNrsUsdcStakingContract: any, address: string) {
        if (this.latestUpdateAtNrsUsdc === 0 || Date.now() - this.latestUpdateAtNrsUsdc > 5000) {
            await this.fetchBalancesNRS_USDC(nrsContract, lpNrsdUsdcContract, lpNrsUsdcStakingContract, address)
        }
    }

    async fetchBalancesNXUSD_USDC(nrsContract: any, lpNxusdUsdcContract: any, lpNxusdUsdcStakingContract: any, address: string) {
        this.latestUpdateAtNxusdUsdc = Date.now()
        // {
        //     const balance = await nrsContract.read.balanceOf([address]);
        //     this.setNrsTokenBalanceDecimal(balance.toString());
        // }
        {
            const balance = await lpNxusdUsdcContract.read.balanceOf([address]);
            this.setLpNxusdUsdcTokenBalanceDecimal(balance.toString());
        }

        await this.fetchNxusdUsdcStakingBalance(lpNxusdUsdcStakingContract, address);
    }

    async fetchNxusdUsdcStakingBalance(lpNxusdUsdcStakingContract: any, address: string) {
        const [amount] = await lpNxusdUsdcStakingContract.read.poolStakers([address]);
        this.setLpNxusdUsdcStakingBalanceDecimal(amount.toString());
    }

    async fetchBalancesNXUSD_USDCThrottled(nrsContract: any, lpNxusdUsdcContract: any, lpNxusdUsdcStakingContract: any, address: string) {
        if (this.latestUpdateAtNxusdUsdc === 0 || Date.now() - this.latestUpdateAtNxusdUsdc > 5000) {
            await this.fetchBalancesNXUSD_USDC(nrsContract, lpNxusdUsdcContract, lpNxusdUsdcStakingContract, address)
        }
    }

}

export const earnBalanceStore = new EarnBalanceStore();
